import React, { useMemo, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { useAppSelector } from '@/store';
import { useToast } from '@/contexts/admin/ToastContext';
import { useAccountColors } from '@/constants/residents/mobile/accountColors';
import { useAccountSectionStyles } from '@/constants/residents/mobile/accountSectionStyles';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { strings } from '@/constants/residents/strings';
import { AvatarCircle } from '@/components/common/AvatarCircle';
import { getDisplayName } from '@/utils/residents/utils';
import { MobileDeleteAccountModal } from '../MobileDeleteAccountModal';

const ResioLogoFaded = RESIDENTS_SVG_COMPONENTS.RESIO_LOGO_FADED;

interface MobileAccountInfoSectionProps {
  onAccountDeleted?: () => void;
}

/**
 * Mobile Account Info Section
 * Displays user avatar, name, email with delete account option
 * Based on Figma design "1. Account info"
 */
export const MobileAccountInfoSection: React.FC<MobileAccountInfoSectionProps> = ({
  onAccountDeleted,
}) => {
  const ac = useAccountColors();
  const sectionStyles = useAccountSectionStyles();
  const styles = useMemo(() => makeStyles(ac), [ac]);
  const { showSuccess } = useToast();
  const user = useAppSelector((s) => (s as any).auth?.user);
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Build display name using shared utility
  const displayName = useMemo(() => {
    return getDisplayName(user?.firstName, user?.lastName) || strings.account.guestAccountName;
  }, [user?.firstName, user?.lastName]);

  const email = user?.email || '';
  const photoUrl = user?.profilePictureUrl || user?.photoUrl || '';

  const handleDeletePress = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleAccountDeleted = useCallback(() => {
    setShowDeleteModal(false);
    showSuccess('Your account has been successfully deleted');
    onAccountDeleted?.();
  }, [showSuccess, onAccountDeleted]);

  return (
    <View style={sectionStyles.container}>
      {/* Header */}
      <View style={sectionStyles.header}>
        <Text style={sectionStyles.headerTitle}>{strings.account.informationTitle}</Text>
      </View>

      {/* Content */}
      <View style={styles.content}>
        {/* Avatar - using reusable AvatarCircle component */}
        <AvatarCircle
          firstName={user?.firstName}
          lastName={user?.lastName}
          photoUrl={photoUrl}
          size={68}
          useHashedColor={false}
          placeholderColor={ac.avatarBackground}
          initialsColor={ac.textPrimary}
          initialsStyle={styles.avatarInitialsStyle}
          showPlaceholderIconIfNoName={true}
        />

        {/* User Info */}
        <View style={styles.userInfo}>
          <Text style={styles.userName}>{displayName}</Text>
          <Text style={styles.userEmail}>{email}</Text>
        </View>
      </View>

      {/* Footer with Delete Account */}
      <View style={styles.footer}>
        <TouchableOpacity onPress={handleDeletePress}>
          <Text style={styles.deleteLink}>{strings.account.deleteAccountLink}</Text>
        </TouchableOpacity>
        
        {/* Resio Logo Watermark */}
        <View style={styles.logoContainer} pointerEvents="none">
          <ResioLogoFaded width={160} height={174} />
        </View>
      </View>

      {/* Delete Account Modal - Figma design */}
      <MobileDeleteAccountModal
        visible={showDeleteModal}
        onClose={handleDeleteModalClose}
        onAccountDeleted={handleAccountDeleted}
      />
    </View>
  );
};

const makeStyles = (ac: ReturnType<typeof useAccountColors>) => StyleSheet.create({
  content: {
    paddingHorizontal: 20,
    paddingVertical: 24,
    gap: 18,
  },
  avatarInitialsStyle: {
    fontSize: 24,
    fontWeight: '700',
    lineHeight: 36,
  },
  userInfo: {
    gap: 2,
  },
  userName: {
    fontSize: 18,
    fontWeight: '600',
    color: ac.textPrimary,
    lineHeight: 27,
  },
  userEmail: {
    fontSize: 14,
    fontWeight: '400',
    color: ac.textPrimary,
    lineHeight: 21,
  },
  footer: {
    flex: 1,
    paddingHorizontal: 20,
    paddingBottom: 16,
    justifyContent: 'flex-end',
    position: 'relative',
    overflow: 'hidden',
  },
  deleteLink: {
    fontSize: 12,
    fontWeight: '500',
    color: ac.textMuted,
    lineHeight: 18,
  },
  logoContainer: {
    position: 'absolute',
    right: -30,
    bottom: -50,
    opacity: 0.3,
  },
});

export default MobileAccountInfoSection;
